import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import type { Form } from '@aurora/shared-generated/types/graphql-schema-types';
import type { EndUserPages } from '@aurora/shared-types/pages/enums';
import { EndUserComponent, EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import React, { useContext, useEffect, useRef } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import useTranslation from '../../useTranslation';
import localStyles from './IdeaSearchBeforePostWidget.module.pcss';
import InputEditForm from '@aurora/shared-client/components/form/InputEditForm/InputEditForm';
import type { FormSpec } from '@aurora/shared-client/components/form/types';
import FormBuilder from '@aurora/shared-client/helpers/form/FormBuilder/FormBuilder';
import type { UrlQuery } from '@aurora/shared-utils/helpers/urls/NextRoutes/Route';
import useQueryParamValueForWidget from '../../useQueryParamValueForWidget';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import type {
  EndUserRouteAndParams,
  MessagePagesAndParams
} from '@aurora/shared-client/routes/endUserRoutes';
import searchBeforePostFormSchema from './SearchBeforePostForm.form.json';
import NodeIcon from '@aurora/shared-client/components/nodes/NodeIcon/NodeIcon';
import { IconSize } from '@aurora/shared-client/components/common/Icon/enums';

interface Props {
  /**
   * Default search term
   */
  defaultSearchTerm?: string;
  /**
   * Whether the search query is in loading state
   */
  loading?: boolean;
  /**
   * Optional class name to set on the search bar
   */
  className?: string;
  /**
   * Callback function when a search is entered
   *
   * @callback
   */
  onChange?: (searchTerm: string) => void;
}

interface IdeaSearchData {
  /**
   * The text to execute a search with
   */
  search: string;
}

/**
 * Search bar for Idea search before post widget
 *
 * @author rishabh.jivan
 */
const IdeaSearchBeforePostSearchBar: React.FC<Props> = props => {
  const { defaultSearchTerm, loading, className, onChange } = props;
  const cx = useClassNameMapper(localStyles);
  const i18n = useTranslation(EndUserComponent.IDEA_SEARCH_BEFORE_POST_WIDGET);
  const { contextNode } = useContext(AppContext);

  const { router } = useEndUserRoutes();
  const { key: queryParamKey, value: queryParamValue } = useQueryParamValueForWidget(
    EndUserQueryParams.SEARCH_KEY,
    ''
  );

  useEffect(() => {
    if (onChange) {
      onChange(queryParamValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParamValue]);

  const searchFormReference = useRef<HTMLFormElement>(null);
  const formSpecs: FormSpec<IdeaSearchData> = new FormBuilder<IdeaSearchData>(
    'EmbeddedSearchForm',
    i18n,
    {
      schema: searchBeforePostFormSchema as Form,
      cx
    },
    { formClassName: cx('lia-embedded-search-form') }
  )
    .addSearchField({
      name: 'search',
      size: 'lg',
      isLoading: loading,
      placeholderText: i18n.formatMessage('searchFieldPlaceholderText'),
      formGroupSpec: {
        className: 'lia-g-mb-0',
        label: false
      },
      focus: null,
      defaultValue: defaultSearchTerm || queryParamValue,
      searchIcon: () => (
        <NodeIcon size={IconSize.PX_24} node={contextNode} className={cx('lia-g-mt-5 mb-auto')} />
      ),
      onKeyDown: (event, formMethods) => {
        async function handleEvent() {
          if (event.key === 'Enter') {
            const { search } = formMethods.getValues();
            const searchTerm = search.trim();
            if (searchTerm.length > 1) {
              const queryParams: Partial<UrlQuery<EndUserQueryParams>> = {
                [queryParamKey]: searchTerm
              };
              await router.replaceRoute<EndUserRouteAndParams<EndUserPages>>(
                router.getCurrentPageName(),
                router.getPathParams<MessagePagesAndParams>(),
                {
                  ...router.getQueryParams(),
                  ...queryParams
                }
              );
            }
          }
        }
        handleEvent();
      },
      onClearSearch: () => {
        async function clearSearch() {
          await router.removeQueryParam([queryParamKey], true, true);
          if (onChange) {
            onChange('');
          }
        }
        clearSearch();
      }
    })
    .build();

  return (
    <div className={cx(className)}>
      <InputEditForm<IdeaSearchData>
        formRef={searchFormReference}
        formSpec={formSpecs}
        onSubmit={(formData: IdeaSearchData): void => {
          if (onChange) {
            onChange(formData.search.trim() || '');
          }
        }}
        submitOnChange={{
          watchFields: ['search']
        }}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...{ resetForm: queryParamValue ? null : { search: '' } }}
      />
    </div>
  );
};

export default IdeaSearchBeforePostSearchBar;
